import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Utils } from '@/utils/utils';

@Component({
  selector: 'app-delivery-note-sign',
  templateUrl: './document.component.html',
  imports: [CentToEurDisplayDirective, DatePipe, DecimalPipe]
})
export class DeliveryNoteSignTemplateComponent implements OnInit, OnDestroy {

  private static readonly AUTO_RETURN_DELAY_MS = 10000;

  public document: any = {}
  public client: any = {}
  public documentLines: any[] = []
  public tax: any = null
  public pay_method: any = null
  id: string | null = null

  /**
   * Present when this page was opened from the tablet-sign flow
   * (TabletSignPage.goToSign(), no login) instead of an admin's own
   * authenticated "Firmar albarán" button - swaps every API call below to the
   * ?token=-gated public-sign endpoints (DocumentsController::getPublicSign/
   * uploadPublicSign/getPublicSignFile) instead of the normal auth:sanctum ones,
   * since a tablet has no Sanctum session for JwtInterceptor to attach.
   */
  private signToken: string | null = null;

  public hasSignature = signal(false);
  public confirmingSignature = signal(false);
  public saving = signal(false);
  public loadingSignature = signal(false);
  public signatureSavedUrl = signal<string | null>(null);

  private ctx: CanvasRenderingContext2D | null = null;
  private autoReturnTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  @ViewChild('signaturePad')
  signatureCanvas!: ElementRef<HTMLCanvasElement>;

  @ViewChild('signDocumentRoot')
  signDocumentRoot!: ElementRef<HTMLElement>;

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    this.signToken = this.route.snapshot.queryParamMap.get('token');
    this.getData(this.id);
  }

  ngOnDestroy(): void {
    this.revokeSavedUrl();
    this.clearAutoReturnTimer();
  }

  private revokeSavedUrl(): void {
    const current = this.signatureSavedUrl();
    if (current) URL.revokeObjectURL(current);
  }

  getDocumentLabel(type: string): string {
    const labels: Record<string, string> = {
      'factura': 'Nº Factura',
      'albaran': 'Nº Albarán',
      'presupuesto': 'Nº Presupuesto'
    };
    return labels[type] ?? '';
  }

  getData(id: any): any {
    const request$ = this.signToken
      ? this.apiService.getPetition(`${Urls.urls.documents}/${id}/public-sign`, `?token=${this.signToken}`)
      : this.apiService.getById(Urls.urls.documents, id);

    request$.subscribe({
      next: (data: any) => {
        this.document = data.document;
        this.client = data.client;
        this.documentLines = data.document_details;
        this.tax = data.tax;
        this.pay_method = data.pay_method;
        this.cdr.detectChanges();

        if (data.document?.sign_url) {
          this.fetchSavedSignature();
        } else {
          setTimeout(() => this.setupCanvas());
        }
      },
      error: (error: any) => {
        console.error('ERROR obtener albarán. ' + error);
      },
    });
  }

  /**
   * `sign_url` points at a private-disk file — it can't be used as a plain <img src>
   * (the request never carries the Sanctum bearer token), so it's fetched authenticated
   * via ApiService/HttpClient and rendered from an object URL instead.
   */
  private fetchSavedSignature(): void {
    this.loadingSignature.set(true);
    const url = this.signToken
      ? `${Urls.urls.documents}/${this.id}/public-sign-file?token=${this.signToken}`
      : `${Urls.urls.documents}/${this.id}/sign`;

    this.apiService.getBlob(url).subscribe({
      next: (blob: Blob) => {
        this.revokeSavedUrl();
        this.loadingSignature.set(false);
        this.signatureSavedUrl.set(URL.createObjectURL(blob));
        this.cdr.detectChanges();
        this.startAutoReturnTimer();
      },
      error: () => {
        this.loadingSignature.set(false);
        this.cdr.detectChanges();
        setTimeout(() => this.setupCanvas());
      },
    });
  }

  /**
   * Once a signature is saved this page is normally sitting unattended on a
   * physical tablet - after AUTO_RETURN_DELAY_MS with no interaction it
   * returns to the tablet's waiting screen on its own. Only relevant to the
   * tablet flow (signToken set); an admin's own authenticated tab has no
   * tablet to go back to.
   */
  private startAutoReturnTimer(): void {
    if (!this.signToken) return;
    this.clearAutoReturnTimer();
    this.autoReturnTimer = setTimeout(() => this.goToTablet(), DeliveryNoteSignTemplateComponent.AUTO_RETURN_DELAY_MS);
  }

  private clearAutoReturnTimer(): void {
    if (this.autoReturnTimer) {
      clearTimeout(this.autoReturnTimer);
      this.autoReturnTimer = null;
    }
  }

  goToTablet(): void {
    this.clearAutoReturnTimer();
    this.router.navigate(['/tablet-sign']);
  }

  /** "Volver a la tablet" only makes sense when this page was opened from the tablet-sign flow. */
  isTabletFlow(): boolean {
    return !!this.signToken;
  }

  private setupCanvas(): void {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;

    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * ratio;
    canvas.height = canvas.clientHeight * ratio;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';

    let drawing = false;
    let lastX = 0;
    let lastY = 0;

    const getPos = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      drawing = true;
      const pos = getPos(e);
      lastX = pos.x;
      lastY = pos.y;
    });

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (!drawing) return;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastX = pos.x;
      lastY = pos.y;
      if (!this.hasSignature()) this.hasSignature.set(true);
    });

    const stopDrawing = () => { drawing = false; };
    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointerleave', stopDrawing);
    canvas.addEventListener('pointercancel', stopDrawing);
  }

  clearSignature(): void {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas || !this.ctx) return;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasSignature.set(false);
  }

  /** Freezes the pad (no more strokes accepted) so the client can review before it's actually uploaded. */
  confirmSignature(): void {
    if (!this.hasSignature()) return;
    this.confirmingSignature.set(true);
    const canvas = this.signatureCanvas?.nativeElement;
    if (canvas) canvas.style.pointerEvents = 'none';
  }

  /** Discards the drawn signature and unfreezes the pad so the client can sign again from scratch. */
  repeatSignature(): void {
    this.confirmingSignature.set(false);
    this.clearSignature();
    const canvas = this.signatureCanvas?.nativeElement;
    if (canvas) canvas.style.pointerEvents = '';
  }

  resign(): void {
    this.clearAutoReturnTimer();
    this.revokeSavedUrl();
    this.signatureSavedUrl.set(null);
    this.hasSignature.set(false);
    this.confirmingSignature.set(false);
    setTimeout(() => this.setupCanvas());
  }

  async saveSignature(): Promise<void> {
    if (!this.hasSignature() || this.saving()) return;

    this.saving.set(true);

    try {
      const pdfBlob = await this.renderSignedPdf();

      const fd = new FormData();
      fd.append('signed_document', pdfBlob, `albaran-${this.id}-firmado.pdf`);

      const url = this.signToken
        ? `${Urls.urls.documents}/${this.id}/public-sign?token=${this.signToken}`
        : `${Urls.urls.documents}/${this.id}/sign`;

      this.apiService.upload(url, fd).subscribe({
        next: (response: any) => {
          this.saving.set(false);
          Utils.notification.success(response?.message ?? 'Firma guardada correctamente');
          this.fetchSavedSignature();
        },
        error: (error: any) => {
          this.saving.set(false);
          Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar la firma');
        },
      });
    } catch {
      this.saving.set(false);
      Utils.notification.alert('Error al generar el PDF de la firma');
    }
  }

  /** Renders the whole albarán (as currently on screen, signature included) to a one-or-more-page A4 PDF. */
  private async renderSignedPdf(): Promise<Blob> {
    const root = this.signDocumentRoot?.nativeElement;
    if (!root) throw new Error('No se encontró el contenido del albarán');

    const controls = root.querySelector<HTMLElement>('.firma-controls');
    const previousDisplay = controls?.style.display ?? '';
    if (controls) controls.style.display = 'none';

    try {
      // scale 1.5 (not 2) + JPEG (not PNG) keeps the PDF well under typical php.ini
      // upload_max_filesize limits (e.g. the 2M WAMP default) — a full-page PNG at
      // scale:2 routinely lands in the 3-6MB range and gets silently truncated by PHP,
      // which Laravel's `uploaded` file-validation rule reports as a 422 "failed to upload".
      const rendered = await html2canvas(root, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = rendered.toDataURL('image/jpeg', 0.85);

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (rendered.height * imgWidth) / rendered.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      return pdf.output('blob');
    } finally {
      if (controls) controls.style.display = previousDisplay;
    }
  }
}
